"use client";

import type * as React from "react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { BookingSearchEditDrawer } from "@/components/booking/BookingSearchEditDrawer";
import { BookingSearchSummary } from "@/components/booking/BookingSearchSummary";
import { BookingStepOneSkeleton } from "@/components/booking/BookingStepOneSkeleton";
import { BookingVehicleFilterDrawer } from "@/components/booking/BookingVehicleFilterDrawer";
import { BookingVehicleGridSkeleton } from "@/components/booking/BookingVehicleGridSkeleton";
import { BookingVehicleList } from "@/components/booking/BookingVehicleList";
import { BookingWizard } from "@/components/booking/BookingWizard";
import {
  parseBookingSearchParams,
  appendSearchToPath,
  type VehicleType,
} from "@/lib/booking/search-params";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import { setSelectedVehicleDetails } from "@/lib/redux/features/user-selected-details/userSelectedDetailsSlice";
import {
  bookingSearchParamsToKey,
  fetchVehicles,
  selectSelectedVehicle,
  selectVehicles,
  selectVehiclesError,
  selectVehiclesSearchKey,
  selectVehiclesStatus,
} from "@/lib/redux/features/vehicles/vehiclesSlice";
import {
  applyVehicleFilters,
  emptyVehicleFilters,
  type VehicleFilters,
} from "@/lib/vehicles/filters";

function BookingStepOneContentInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const vehicles = useAppSelector(selectVehicles);
  const selectedVehicle = useAppSelector(selectSelectedVehicle);
  const status = useAppSelector(selectVehiclesStatus);
  const error = useAppSelector(selectVehiclesError);
  const vehiclesSearchKey = useAppSelector(selectVehiclesSearchKey);
  const vehicleTab = searchParams.get("vehicle") as VehicleType | null;
  const [filters, setFilters] = useState<VehicleFilters>(emptyVehicleFilters);
  const [draftFilters, setDraftFilters] =
    useState<VehicleFilters>(emptyVehicleFilters);

  const bookingParams = useMemo(
    () => parseBookingSearchParams(searchParams),
    [searchParams],
  );

  const currentSearchKey = useMemo(
    () => (bookingParams ? bookingSearchParamsToKey(bookingParams) : null),
    [bookingParams],
  );

  useEffect(() => {
    if (!bookingParams || !currentSearchKey) {
      return;
    }

    if (status === "loading" || vehiclesSearchKey === currentSearchKey) {
      return;
    }

    void dispatch(fetchVehicles(bookingParams));
  }, [
    bookingParams,
    currentSearchKey,
    dispatch,
    status,
    vehiclesSearchKey,
  ]);

  const filteredVehicles = useMemo(
    () => applyVehicleFilters(vehicles, filters, vehicleTab),
    [vehicles, filters, vehicleTab],
  );

  const draftResultCount = useMemo(
    () => applyVehicleFilters(vehicles, draftFilters, vehicleTab).length,
    [vehicles, draftFilters, vehicleTab],
  );

  const handleApplyFilters = () => {
    setFilters(draftFilters);
  };

  const handleClearFilters = () => {
    setDraftFilters(emptyVehicleFilters);
    setFilters(emptyVehicleFilters);
  };

  const isLoading =
    !bookingParams || status === "idle" || status === "loading";

  const selectVehicleHref = useMemo(() => {
    if (!selectedVehicle) {
      return null;
    }

    const nextSearch = new URLSearchParams(searchParams.toString());
    nextSearch.set("selectedVehicleTypeId", String(selectedVehicle.type_id));
    return appendSearchToPath("/booking/offercheckout", nextSearch);
  }, [searchParams, selectedVehicle]);

  const handleSelectVehicle = () => {
    if (!selectedVehicle || !selectVehicleHref) {
      return;
    }

    dispatch(setSelectedVehicleDetails(selectedVehicle));
    router.push(selectVehicleHref);
  };

  const headerAction =
    selectedVehicle !== null ? (
      <button
        type="button"
        onClick={handleSelectVehicle}
        className="inline-flex h-12 min-w-36 items-center justify-center rounded-xl bg-blue-500 px-8 text-base font-semibold text-white transition-colors hover:bg-blue-600"
      >
        Select
      </button>
    ) : undefined;

  return (
    <BookingWizard step={1} headerAction={headerAction}>
      <div className="flex items-end gap-5 px-12 mt-2">
        <div className="flex items-end gap-2">
          <div className="flex flex-col">
            <Suspense
              fallback={
                <p className="text-gray-900 text-sm">Loading search…</p>
              }
            >
              <BookingSearchSummary />
            </Suspense>
          </div>
          <Suspense fallback={null}>
            <BookingSearchEditDrawer />
          </Suspense>
        </div>
        <BookingVehicleFilterDrawer
          resultCount={draftResultCount}
          appliedFilters={filters}
          draftFilters={draftFilters}
          onDraftChange={setDraftFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </div>

      {!bookingParams ? (
        <p className="px-8 py-12 text-center text-sm text-gray-500">
          Start a search from the home page to see available vehicles.
        </p>
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-6 px-12 py-6 md:grid-cols-2 xl:grid-cols-3">
          <BookingVehicleGridSkeleton />
        </div>
      ) : status === "failed" ? (
        <p className="px-8 py-12 text-center text-sm text-destructive">
          {error ?? "Unable to load vehicles."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 px-12 py-6 md:grid-cols-2 xl:grid-cols-3">
          <BookingVehicleList vehicles={filteredVehicles} />
        </div>
      )}
    </BookingWizard>
  );
}

export const BookingStepOneContent: React.FC = () => {
  return (
    <Suspense fallback={<BookingStepOneSkeleton />}>
      <BookingStepOneContentInner />
    </Suspense>
  );
};
